
import { useState, useEffect } from 'react';
import { fetchCandidatesForJob, updateCandidateStage, addCandidateToJob } from '@/components/jobs/kanban/utils/candidateOperations';
import { KANBAN_STAGES, KanbanCandidate } from '@/components/jobs/kanban/types/index';
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useHistoryOperations } from "@/hooks/useHistoryOperations";

export const useJobKanban = (jobId: string | undefined) => {
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState<KanbanCandidate[]>([]);
  const [isAddCandidateOpen, setIsAddCandidateOpen] = useState(false);
  const { logHistory } = useHistoryOperations('job');
  
  // Group candidates by their stage
  const candidatesByStage = KANBAN_STAGES.reduce((acc, stage) => {
    acc[stage.id] = candidates.filter(c => c.stage === stage.id);
    return acc;
  }, {} as Record<string, KanbanCandidate[]>);
  
  // Initial fetch of candidates
  const fetchCandidates = async () => {
    if (!jobId) return;
    
    setLoading(true);
    const data = await fetchCandidatesForJob(jobId);
    setCandidates(data);
    setLoading(false);
  };
  
  useEffect(() => {
    if (jobId) {
      fetchCandidates();
    }
  }, [jobId]);
  
  // Handle stage change when a candidate is dragged to a new column
  const handleStageChange = async (candidateId: string, newStage: string) => {
    // Find the match to update
    const matchToUpdate = candidates.find(c => c.id === candidateId);
    
    if (!matchToUpdate || !jobId) return;
    
    const oldStage = matchToUpdate.stage;
    
    // Only proceed if there's an actual change
    if (oldStage === newStage) return;
    
    // Update in the database
    const success = await updateCandidateStage(matchToUpdate.id, newStage);
    
    if (success) {
      // Update local state
      setCandidates(candidates.map(c => 
        c.id === candidateId ? { ...c, stage: newStage } : c
      ));
      
      // Log the stage change in job history
      try {
        await logHistory({
          entityId: jobId,
          entityType: 'job',
          action: 'updated',
          description: `Candidate ${matchToUpdate.name} stage changed`,
          change: {
            field: 'stage',
            oldValue: oldStage,
            newValue: newStage
          }
        });
      } catch (error) {
        console.error("Error logging job history:", error);
      }
    }
  };
  
  // Open the add candidate dialog
  const handleAddCandidate = () => {
    setIsAddCandidateOpen(true);
  };
  
  // Add a candidate to the job
  const handleAddCandidateConfirm = async (candidateId: string) => {
    setIsAddCandidateOpen(false);
    
    if (!candidateId || !jobId) return;
    
    // Get candidate name if possible
    let candidateName = "a candidate";
    try {
      const { data } = await supabase
        .from('candidates')
        .select('name')
        .eq('id', candidateId)
        .single();
      
      if (data) {
        candidateName = data.name;
      }
    } catch (error) {
      console.error("Error fetching candidate name:", error);
    }
    
    const success = await addCandidateToJob(jobId, candidateId);
    
    if (success) {
      // Log the action in job history
      try {
        await logHistory({
          entityId: jobId,
          entityType: 'job',
          action: 'created',
          description: `Candidate added to job`,
          change: {
            field: 'candidate',
            oldValue: '',
            newValue: candidateName
          }
        });
      } catch (error) {
        console.error("Error logging job history:", error);
      }
      
      // Refresh candidates list
      fetchCandidates();
    }
  };
  
  // Add multiple candidates to the job
  const addMultipleCandidates = async (candidateIds: string[]) => {
    if (!jobId || candidateIds.length === 0) return;
    
    try {
      // First check if any of these candidates are already matched with the job
      const { data: existingMatches, error: checkError } = await supabase
        .from('matches')
        .select('candidate_id')
        .eq('job_id', jobId)
        .in('candidate_id', candidateIds);
      
      if (checkError) throw checkError;
      
      // Filter out candidates that already have matches
      const existingIds = new Set(existingMatches?.map(match => match.candidate_id) || []);
      const newCandidateIds = candidateIds.filter(id => !existingIds.has(id));
      
      if (newCandidateIds.length === 0) {
        toast({
          title: "Info",
          description: "All selected candidates are already matched to this job."
        });
        return true;
      }
      
      // Create match entries for each new candidate
      const matchEntries = newCandidateIds.map(candidateId => ({
        job_id: jobId,
        candidate_id: candidateId,
        score: 0, // Default score
        status: 'matched' // Default status
      }));
      
      const { error } = await supabase.from('matches').insert(matchEntries);
      
      if (error) throw error;
      
      // Log the action in job history
      await logHistory({
        entityId: jobId,
        entityType: 'job',
        action: 'created',
        description: `Multiple candidates added to job`,
        change: {
          field: 'candidates',
          oldValue: '',
          newValue: `${newCandidateIds.length} candidates`
        }
      });
      
      toast({
        title: "Success",
        description: `${newCandidateIds.length} candidate(s) added to job`,
      });
      
      // Refresh candidates list
      fetchCandidates();
      
      return true;
    } catch (error: any) {
      console.error("Error adding multiple candidates:", error);
      toast({
        title: "Error",
        description: "Failed to add candidates to job",
        variant: "destructive",
      });
      return false;
    }
  };
  
  return {
    loading,
    candidates,
    candidatesByStage,
    kanbanStages: KANBAN_STAGES,
    isAddCandidateOpen,
    setIsAddCandidateOpen,
    handleStageChange,
    handleAddCandidate,
    handleAddCandidateConfirm,
    addMultipleCandidates,
    fetchCandidates
  };
};
